import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { LEGAL_CONSTANTS, TermsAcceptanceData } from '@/constants/legal';

export function useTermsAcceptance() {
  const [needsAcceptance, setNeedsAcceptance] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [acceptanceData, setAcceptanceData] = useState<TermsAcceptanceData | null>(null);
  const [checkCount, setCheckCount] = useState(0);

  useEffect(() => {
    // ✅ PROTEÇÃO: Limitar verificações para evitar loop infinito
    if (checkCount > 3) {
      console.warn('⚠️ Proteção: Muitas verificações de termos. Assumindo que não precisa aceitar.');
      setNeedsAcceptance(false);
      setLoading(false);
      return;
    }
    
    checkTermsAcceptance();
  }, []);

  const checkTermsAcceptance = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      // Buscar a empresa do usuário
      const { data: userEmpresa, error: userEmpresaError } = await supabase
        .from('user_empresas')
        .select('empresa_id')
        .eq('user_id', user.id)
        .single();

      if (userEmpresaError || !userEmpresa) {
        console.error('Empresa do usuário não encontrada');
        setLoading(false);
        return;
      }

      // Se houver override local, não exigir aceite novamente
      const overrideKey = `${LEGAL_CONSTANTS.TERMS_ACCEPTANCE_OVERRIDE_PREFIX}:${userEmpresa.empresa_id}`;
      try {
        if (typeof window !== 'undefined') {
          const override = window.localStorage.getItem(overrideKey);
          if (override === 'true') {
            setNeedsAcceptance(false);
            setLoading(false);
            return;
          }
        }
      } catch (error) {
        console.warn('Não foi possível acessar o localStorage para override de termos:', error);
      }

      // Buscar dados de aceite da empresa
      const { data: empresa, error: empresaError } = await supabase
        .from('empresas')
        .select(`
          accepted_terms,
          accepted_privacy,
          accepted_terms_at,
          accepted_privacy_at,
          terms_version,
          privacy_version
        `)
        .eq('id', userEmpresa.empresa_id)
        .single();

      if (empresaError) {
        // Se as colunas não existem ainda, considerar que precisa aceitar
        console.warn('Colunas de termos não encontradas, forçando aceite:', empresaError);
        setNeedsAcceptance(true);
        setLoading(false);
        return;
      }

      const data = empresa as unknown as TermsAcceptanceData;
      setAcceptanceData(data);

      // Verificar se precisa aceitar a versão atual
      const needsAccept = !data.accepted_terms ||
                         !data.accepted_privacy ||
                         data.terms_version !== LEGAL_CONSTANTS.TERMS_VERSION ||
                         data.privacy_version !== LEGAL_CONSTANTS.PRIVACY_VERSION;

      console.log('📋 Verificação de termos:', {
        accepted_terms: data.accepted_terms,
        accepted_privacy: data.accepted_privacy,
        terms_version: data.terms_version,
        current_terms_version: LEGAL_CONSTANTS.TERMS_VERSION,
        privacy_version: data.privacy_version,
        current_privacy_version: LEGAL_CONSTANTS.PRIVACY_VERSION,
        needsAccept
      });

      setNeedsAcceptance(needsAccept);
    } catch (error) {
      console.error('Erro ao verificar aceite dos termos:', error);
      setNeedsAcceptance(true); // Em caso de erro, forçar aceite para segurança
    } finally {
      setLoading(false);
    }
  };

  const refreshAcceptance = () => {
    console.log('✅ Aceitação de termos confirmada - fechando modal');
    // ✅ CORREÇÃO: Definitivamente definir como não precisa aceitar
    setNeedsAcceptance(false);
    setLoading(false);
    // NÃO chamar checkTermsAcceptance() novamente para evitar loop
  };

  return {
    needsAcceptance,
    loading,
    acceptanceData,
    refreshAcceptance,
  };
}